import React, { useEffect, useState } from 'react';
import {
  Card, Button, Input, Select, Table, Tag, Statistic, Row, Col, Typography,
  Alert, Popconfirm, Tabs, Space, Spin, theme,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ColumnWidthOutlined, PlusOutlined, MinusOutlined, DeleteOutlined,
  PrinterOutlined, CalculatorOutlined, CheckCircleOutlined, CloseCircleOutlined,
  HistoryOutlined, ToolOutlined, DownOutlined, UpOutlined, ApartmentOutlined,
  SlidersOutlined,
} from '@ant-design/icons';
import {
  DaltResult, RectangularDuct, RoundDuct, OvalDuct, TransitionDuct,
  RadialElbow, RectangularElbow, RoundElbow,
} from '../../models/types';
import { saveDaltResult, getDaltResults, deleteDaltResult } from '../../firebase/firestore';
import { runDaltCalculation, calcRectangularDuctArea, calcRoundDuctArea, calcOvalDuctArea, calcTransitionDuctArea, calcRadialElbowArea as calcRadialArea_, calcRectangularElbowArea as calcRectElbowArea_, calcRoundElbowArea as calcRoundElbowArea_ } from './daltCalculations';

interface Props { projectId: string; }

type DuctTab = 'rectangular' | 'round' | 'oval' | 'transition' | 'radial-elbow' | 'rectangular-elbow' | 'round-elbow';

const DUCT_TABS: { id: DuctTab; label: string }[] = [
  { id: 'rectangular', label: 'Rectangular' },
  { id: 'round', label: 'Round' },
  { id: 'oval', label: 'Oval' },
  { id: 'transition', label: 'Transition' },
  { id: 'radial-elbow', label: 'Radial Elbow' },
  { id: 'rectangular-elbow', label: 'Rect. Elbow' },
  { id: 'round-elbow', label: 'Round Elbow' },
];

// fix B-3: UI helpers delegate to engine (div 144 = sq-in to sq-ft)
const calcRectArea = (d: RectangularDuct) => calcRectangularDuctArea(d) / 144;
const calcRoundArea = (d: RoundDuct) => calcRoundDuctArea(d) / 144;
const calcOvalArea = (d: OvalDuct) => calcOvalDuctArea(d) / 144;
const calcTransArea = (d: TransitionDuct) => calcTransitionDuctArea(d) / 144;
const calcRadialArea = (e: RadialElbow) => calcRadialArea_(e) / 144;
const calcRectElbowArea = (e: RectangularElbow) => calcRectElbowArea_(e) / 144;
const calcRoundElbowArea = (e: RoundElbow) => calcRoundElbowArea_(e) / 144;

// Calibration constants from original dalt-web / powerCalc.xlsm
const CALIBRATION_CONSTANTS: Record<string, number> = {
  '0.5': 13.5,
  '1': 27.2,
  '1.5': 40.8,
  '2': 54.3,
  '3': 81.5,
  '4': 108.7,
  '5': 135.8,
  '6': 163.0,
};

const n = (v: string) => parseFloat(v) || 0;

const NInput: React.FC<{ label: string; value: string | number; onChange: (v: string) => void; placeholder?: string }> = ({ label, value, onChange, placeholder }) => (
  <div style={{ flex: '1 1 110px', minWidth: 110 }}>
    <div style={{ fontSize: 13, marginBottom: 6 }}>{label}</div>
    <Input type="number" value={value} placeholder={placeholder} onChange={e => onChange(e.target.value)} />
  </div>
);

function useItemList<T>(empty: () => T) {
  const [items, setItems] = useState<T[]>([]);
  const add = () => setItems(prev => [...prev, empty()]);
  const remove = (i: number) => setItems(prev => prev.filter((_, idx) => idx !== i));
  const update = (i: number, patch: Partial<T>) => setItems(prev => prev.map((x, idx) => idx === i ? { ...x, ...patch } : x));
  const reset = () => setItems([]);
  return { items, add, remove, update, reset };
}

// Generate printable report HTML
const generateReportHtml = (r: DaltResult): string => {
  const p = r.testParameters;
  const res = r.testResults;
  if (!res) return '';

  const ductRows = [
    ...r.rectangularDucts.map(d => `<tr><td>Rectangular</td><td>${d.width}" × ${d.height}"</td><td>${d.length}"</td><td>${calcRectArea(d).toFixed(2)} ft²</td></tr>`),
    ...r.roundDucts.map(d => `<tr><td>Round</td><td>Ø${d.diameter}"</td><td>${d.length}"</td><td>${calcRoundArea(d).toFixed(2)} ft²</td></tr>`),
    ...r.ovalDucts.map(d => `<tr><td>Oval</td><td>${d.majorAxis}" × ${d.minorAxis}"</td><td>${d.length}"</td><td>${calcOvalArea(d).toFixed(2)} ft²</td></tr>`),
    ...r.transitionDucts.map(d => `<tr><td>Transition</td><td>${d.inletWidth}"×${d.inletHeight}" → ${d.outletWidth}"×${d.outletHeight}"</td><td>${d.length}"</td><td>${calcTransArea(d).toFixed(2)} ft²</td></tr>`),
    ...r.radialElbows.map(d => `<tr><td>Radial Elbow</td><td>IR=${d.insideRadius}" OR=${d.outsideRadius}" ${d.elbowAngle}°</td><td>Qty: ${d.quantity}</td><td>${calcRadialArea(d).toFixed(2)} ft²</td></tr>`),
    ...r.rectangularElbows.map(d => `<tr><td>Rect. Elbow</td><td>${d.overallWidth}"×${d.overallLength}"</td><td>Qty: ${d.quantity}</td><td>${calcRectElbowArea(d).toFixed(2)} ft²</td></tr>`),
    ...r.roundElbows.map(d => `<tr><td>Round Elbow</td><td>Ø${d.diameter}" R=${d.radius}" ${d.angle}°</td><td>Qty: ${d.quantity}</td><td>${calcRoundElbowArea(d).toFixed(2)} ft²</td></tr>`),
  ].join('');

  return `<!DOCTYPE html>
<html><head><meta charset="UTF-8"><title>DALT Report — ${r.sectionLabel || 'Untitled'}</title>
<style>
  body { font-family: Arial, sans-serif; font-size: 11px; margin: 20px; color: #222; }
  h1 { font-size: 16px; border-bottom: 2px solid #333; padding-bottom: 6px; }
  h2 { font-size: 13px; background: #f0f0f0; padding: 4px 8px; margin-top: 16px; }
  table { width: 100%; border-collapse: collapse; margin: 8px 0; }
  th { background: #333; color: white; padding: 5px 8px; text-align: left; }
  td { padding: 4px 8px; border-bottom: 1px solid #ddd; }
  .pass { color: #155724; font-weight: bold; }
  .fail { color: #721c24; font-weight: bold; }
  .result-box { border: 2px solid #333; padding: 10px; margin: 12px 0; text-align: center; font-size: 14px; font-weight: bold; }
  .pass-box { border-color: #28a745; color: #155724; }
  .fail-box { border-color: #dc3545; color: #721c24; }
  .footer { margin-top: 24px; font-size: 10px; color: #666; border-top: 1px solid #ccc; padding-top: 8px; }
  @media print { body { margin: 10px; } }
</style>
</head><body>
<h1>🌀 Duct Air Leakage Test (DALT) Report</h1>
<p><strong>Section:</strong> ${r.sectionLabel || 'Untitled'} &nbsp;&nbsp; <strong>Date:</strong> ${p.testDate} &nbsp;&nbsp; <strong>Witnessed By:</strong> ${p.witnessedBy || '—'}</p>

<h2>Test Parameters</h2>
<table>
  <tr><th>Parameter</th><th>Value</th><th>Parameter</th><th>Value</th></tr>
  <tr><td>Test Pressure</td><td>${p.testPressure}" WC</td><td>Leakage Class</td><td>${p.leakageClass}</td></tr>
  <tr><td>Pressure Drop</td><td>${p.pressureDrop}" WC</td><td>Orifice Size</td><td>${p.orificeSize}"</td></tr>
  <tr><td>Calibration Constant</td><td>${p.calibrationConstant ?? '—'}</td><td>Orifice Serial</td><td>${p.orificeSerial || '—'}</td></tr>
</table>

<h2>Test Equipment</h2>
<table>
  <tr><th>Field</th><th>Value</th><th>Field</th><th>Value</th></tr>
  <tr><td>Manufacturer</td><td>${p.equipmentManufacturer || '—'}</td><td>Model Number</td><td>${p.equipmentModel || '—'}</td></tr>
  <tr><td>Serial Number</td><td>${p.equipmentSerial || '—'}</td><td>Last Calibration</td><td>${p.equipmentLastCalibration || '—'}</td></tr>
  <tr><td>Next Calibration</td><td>${p.equipmentNextCalibration || '—'}</td><td></td><td></td></tr>
</table>

<h2>Duct Sections</h2>
<table>
  <tr><th>Type</th><th>Dimensions</th><th>Length / Qty</th><th>Surface Area</th></tr>
  ${ductRows || '<tr><td colspan="4">No ducts entered</td></tr>'}
  <tr style="font-weight:bold; background:#f0f0f0;"><td colspan="3">Total Surface Area</td><td>${(res.surfaceArea/144).toFixed(2)} ft²</td></tr>
</table>

<h2>Test Results</h2>
<table>
  <tr><th>Parameter</th><th>Value</th><th>Parameter</th><th>Value</th></tr>
  <tr><td>Total Surface Area</td><td>${(res.surfaceArea/144).toFixed(2)} ft²</td><td>Leakage Factor</td><td>${res.leakageFactor.toFixed(4)} CFM/100 ft²</td></tr>
  <tr><td>Allowable Leakage</td><td>${res.allowableLeakage.toFixed(2)} CFM</td><td>Actual Leakage</td><td>${res.actualLeakage.toFixed(2)} CFM</td></tr>
  <tr><td>% of Allowable</td><td>${res.percentOfAllowable.toFixed(1)}%</td><td>Test Leakage Class</td><td>${res.testLeakageClass.toFixed(2)}</td></tr>
</table>

<div class="result-box ${res.passed ? 'pass-box' : 'fail-box'}">
  TEST ${res.passed ? 'PASS ✓' : 'FAIL ✗'} — ${res.percentOfAllowable.toFixed(1)}% of Allowable Leakage
</div>

${r.notes ? `<p><strong>Notes:</strong> ${r.notes}</p>` : ''}

<div class="footer">
  SMACNA HVAC Air Duct Leakage Test Manual · Generated by MEP Tools · ${new Date().toLocaleDateString()}
</div>
<script>window.onload = () => window.print();</script>
</body></html>`;
};

interface DetailRow { key: string; type: string; dims: string; area: number; }

const detailRows = (r: DaltResult): DetailRow[] => [
  ...r.rectangularDucts.map((d, i) => ({ key: `r${i}`, type: 'Rect', dims: `${d.width}"×${d.height}"×${d.length}"`, area: calcRectArea(d) })),
  ...r.roundDucts.map((d, i) => ({ key: `ro${i}`, type: 'Round', dims: `Ø${d.diameter}"×${d.length}"`, area: calcRoundArea(d) })),
  ...r.ovalDucts.map((d, i) => ({ key: `ov${i}`, type: 'Oval', dims: `${d.majorAxis}"×${d.minorAxis}"×${d.length}"`, area: calcOvalArea(d) })),
  ...r.transitionDucts.map((d, i) => ({ key: `t${i}`, type: 'Trans', dims: `${d.inletWidth}"×${d.inletHeight}"→${d.outletWidth}"×${d.outletHeight}"×${d.length}"`, area: calcTransArea(d) })),
  ...r.radialElbows.map((d, i) => ({ key: `ra${i}`, type: 'Radial Elbow', dims: `IR=${d.insideRadius}" OR=${d.outsideRadius}" ${d.elbowAngle}° ×${d.quantity}`, area: calcRadialArea(d) })),
  ...r.rectangularElbows.map((d, i) => ({ key: `re${i}`, type: 'Rect Elbow', dims: `${d.overallWidth}"×${d.overallLength}" ×${d.quantity}`, area: calcRectElbowArea(d) })),
  ...r.roundElbows.map((d, i) => ({ key: `rne${i}`, type: 'Round Elbow', dims: `Ø${d.diameter}" R=${d.radius}" ${d.angle}° ×${d.quantity}`, area: calcRoundElbowArea(d) })),
];

const detailColumns: ColumnsType<DetailRow> = [
  { title: 'Type', dataIndex: 'type', width: 130 },
  { title: 'Dimensions', dataIndex: 'dims' },
  { title: 'Surface Area', dataIndex: 'area', align: 'right', render: (v: number) => `${v.toFixed(2)} ft²` },
];

const DaltModule: React.FC<Props> = ({ projectId }) => {
  const { token } = theme.useToken();
  const [results, setResults] = useState<DaltResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string,string>>({});
  const [customOrifice, setCustomOrifice] = useState('');
  const [ductTab, setDuctTab] = useState<DuctTab>('rectangular');
  const [showEquipment, setShowEquipment] = useState(false);
  const [livePreview, setLivePreview] = useState<any>(null);
  const [expandedResults, setExpandedResults] = useState<Record<string, boolean>>({});

  // Test params
  const [label, setLabel] = useState('');
  const [testPressure, setTestPressure] = useState('1.0');
  const [leakageClass, setLeakageClass] = useState('6');
  const [pressureDrop, setPressureDrop] = useState('');
  const [orificeSize, setOrificeSize] = useState('');
  const [calibConst, setCalibConst] = useState('');
  const [testDate, setTestDate] = useState(new Date().toISOString().split('T')[0]);
  const [witnessedBy, setWitnessedBy] = useState('');
  const [notes, setNotes] = useState('');

  // Equipment fields
  const [eqMfr, setEqMfr] = useState('');
  const [eqModel, setEqModel] = useState('');
  const [eqSerial, setEqSerial] = useState('');
  const [eqLastCal, setEqLastCal] = useState('');
  const [eqNextCal, setEqNextCal] = useState('');
  const [orificeSerial, setOrificeSerial] = useState('');

  // Duct lists
  const rect = useItemList<RectangularDuct>(() => ({ width: 0, height: 0, length: 0 }));
  const round = useItemList<RoundDuct>(() => ({ diameter: 0, length: 0 }));
  const oval = useItemList<OvalDuct>(() => ({ majorAxis: 0, minorAxis: 0, length: 0 }));
  const trans = useItemList<TransitionDuct>(() => ({ inletWidth: 0, inletHeight: 0, outletWidth: 0, outletHeight: 0, length: 0 }));
  const radial = useItemList<RadialElbow>(() => ({ inletWidth: 0, outletWidth: 0, insideRadius: 0, outsideRadius: 0, inletHeight: 0, outletHeight: 0, elbowAngle: 90, quantity: 1 }));
  const rectElbow = useItemList<RectangularElbow>(() => ({ overallWidth: 0, inletWidth: 0, outletWidth: 0, offsetWidth: 0, overallLength: 0, inletHeight: 0, outletHeight: 0, offsetLength: 0, quantity: 1 }));
  const roundElbow = useItemList<RoundElbow>(() => ({ diameter: 0, radius: 0, angle: 90, quantity: 1 }));

  const totalDuctCount = rect.items.length + round.items.length + oval.items.length + trans.items.length +
    radial.items.length + rectElbow.items.length + roundElbow.items.length;

  useEffect(() => {
    getDaltResults(projectId)
      .then(setResults).catch(() => setError('Failed to load')).finally(() => setLoading(false));
  }, [projectId]);

  // Auto-populate calibration constant when orifice size changes
  const handleOrificeSizeChange = (val: string) => {
    setOrificeSize(val);
    const lookup = CALIBRATION_CONSTANTS[val];
    if (lookup !== undefined) setCalibConst(String(lookup));
  };

  // Live preview — recalculate whenever ducts or test params change
  useEffect(() => {
    if (totalDuctCount === 0 || !pressureDrop || !calibConst) {
      setLivePreview(null);
      return;
    }
    try {
      const preview = runDaltCalculation(
        rect.items, round.items, oval.items, trans.items,
        radial.items, rectElbow.items, roundElbow.items,
        n(testPressure), n(leakageClass), n(pressureDrop), n(calibConst),
      );
      setLivePreview(preview);
    } catch { setLivePreview(null); }
  }, [totalDuctCount, rect.items, round.items, oval.items, trans.items, radial.items, rectElbow.items, roundElbow.items,
    testPressure, leakageClass, pressureDrop, calibConst]);

  const resetForm = () => {
    setLabel(''); setPressureDrop(''); setCalibConst(''); setNotes(''); setOrificeSize(''); setCustomOrifice(''); setFieldErrors({});
    setWitnessedBy(''); setEqMfr(''); setEqModel(''); setEqSerial('');
    setEqLastCal(''); setEqNextCal(''); setOrificeSerial('');
    rect.reset(); round.reset(); oval.reset(); trans.reset();
    radial.reset(); rectElbow.reset(); roundElbow.reset();
    setLivePreview(null);
  };

  const validateForm = (): boolean => {
    const errs: Record<string,string> = {};
    if (totalDuctCount === 0) errs.ducts = 'Add at least one duct or elbow.';
    if (!pressureDrop || n(pressureDrop) <= 0) errs.pressureDrop = 'Required — must be > 0';
    if (!calibConst || n(calibConst) <= 0) errs.calibConst = 'Required — must be > 0';
    if (!testPressure || n(testPressure) <= 0) errs.testPressure = 'Required — must be > 0';
    if (!leakageClass || n(leakageClass) <= 0) errs.leakageClass = 'Required — must be > 0';
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) setError(Object.values(errs)[0]);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setSaving(true); setError('');
    try {
      const testResults = runDaltCalculation(
        rect.items, round.items, oval.items, trans.items,
        radial.items, rectElbow.items, roundElbow.items,
        n(testPressure), n(leakageClass), n(pressureDrop), n(calibConst),
      );
      const entry: Omit<DaltResult, 'id'> = {
        projectId, sectionLabel: label,
        testParameters: {
          testPressure: n(testPressure), leakageClass: n(leakageClass),
          pressureDrop: n(pressureDrop), orificeSize, testDate, witnessedBy,
          ductSectionId: '',
          calibrationConstant: n(calibConst),
          equipmentManufacturer: eqMfr, equipmentModel: eqModel,
          equipmentSerial: eqSerial, equipmentLastCalibration: eqLastCal,
          equipmentNextCalibration: eqNextCal, orificeSerial,
        },
        rectangularDucts: rect.items, roundDucts: round.items, ovalDucts: oval.items,
        transitionDucts: trans.items, radialElbows: radial.items,
        rectangularElbows: rectElbow.items, roundElbows: roundElbow.items,
        testResults, notes, createdAt: new Date().toISOString(),
      };
      const id = await saveDaltResult(entry);
      setResults(prev => [{ ...entry, id }, ...prev]);
      setSaveSuccess(true);
      setTimeout(() => { setSaveSuccess(false); resetForm(); }, 1500);
    } catch (err: any) {
      setError(`Failed to save: ${err?.message ?? err?.code ?? 'Unknown error'}`);
    } finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    try { await deleteDaltResult(projectId, id); setResults(prev => prev.filter(r => r.id !== id)); }
    catch { setError('Failed to delete'); }
  };

  const handlePrint = (r: DaltResult) => {
    const html = generateReportHtml(r);
    const win = window.open('', '_blank');
    if (win) { win.document.write(html); win.document.close(); }
  };

  const ductTotal =
    rect.items.reduce((s,d)=>s+calcRectArea(d),0)+
    round.items.reduce((s,d)=>s+calcRoundArea(d),0)+
    oval.items.reduce((s,d)=>s+calcOvalArea(d),0)+
    trans.items.reduce((s,d)=>s+calcTransArea(d),0)+
    radial.items.reduce((s,e)=>s+calcRadialArea(e),0)+
    rectElbow.items.reduce((s,e)=>s+calcRectElbowArea(e),0)+
    roundElbow.items.reduce((s,e)=>s+calcRoundElbowArea(e),0);
  const paramGrid: React.CSSProperties = { display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' };
  const rowStyle: React.CSSProperties = { display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 10 };

  const paramField = (labelText: React.ReactNode, node: React.ReactNode, errKey?: string, span?: boolean) => (
    <div style={span ? { gridColumn: '1 / -1' } : undefined}>
      <div style={{ fontSize: 13, marginBottom: 6 }}>{labelText}</div>
      {node}
      {errKey && fieldErrors[errKey] && (
        <Typography.Text type="danger" style={{ fontSize: 12 }}>{fieldErrors[errKey]}</Typography.Text>
      )}
    </div>
  );

  const removeBtn = (onClick: () => void) => (
    <Button danger icon={<MinusOutlined />} onClick={onClick} aria-label="Remove row" style={{ marginBottom: 1 }} />
  );

  const subtotal = (value: number) => (
    <Typography.Text type="secondary" style={{ fontSize: 13, display: 'block', marginTop: 4 }}>
      Subtotal: <Typography.Text strong>{value.toFixed(2)} ft²</Typography.Text>
    </Typography.Text>
  );

  const emptyRows = (text: string) => (
    <Typography.Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 10 }}>{text}</Typography.Text>
  );

  const addBtn = (text: string, onClick: () => void) => (
    <Button type="dashed" icon={<PlusOutlined />} onClick={onClick} style={{ marginTop: 10 }}>{text}</Button>
  );

  const tabContent: Record<DuctTab, React.ReactNode> = {
    rectangular: (
      <div>
        {rect.items.length === 0 && emptyRows('No rectangular ducts added.')}
        {rect.items.map((d, i) => (
          <div key={i} style={rowStyle}>
            <NInput label="Width (in)" value={d.width || ''} onChange={v => rect.update(i, { width: n(v) })} />
            <NInput label="Height (in)" value={d.height || ''} onChange={v => rect.update(i, { height: n(v) })} />
            <NInput label="Length (in)" value={d.length || ''} onChange={v => rect.update(i, { length: n(v) })} />
            <Typography.Text type="secondary" style={{ minWidth: 70, paddingBottom: 6 }}>
              {d.width && d.height && d.length ? `${calcRectArea(d).toFixed(1)} ft²` : ''}
            </Typography.Text>
            {removeBtn(() => rect.remove(i))}
          </div>
        ))}
        {rect.items.length > 0 && subtotal(rect.items.reduce((s,d)=>s+calcRectArea(d),0))}
        {addBtn('Add Rectangular Duct', rect.add)}
      </div>
    ),
    round: (
      <div>
        {round.items.length === 0 && emptyRows('No round ducts added.')}
        {round.items.map((d, i) => (
          <div key={i} style={rowStyle}>
            <NInput label="Diameter (in)" value={d.diameter || ''} onChange={v => round.update(i, { diameter: n(v) })} />
            <NInput label="Length (in)" value={d.length || ''} onChange={v => round.update(i, { length: n(v) })} />
            <Typography.Text type="secondary" style={{ minWidth: 70, paddingBottom: 6 }}>
              {d.diameter && d.length ? `${calcRoundArea(d).toFixed(1)} ft²` : ''}
            </Typography.Text>
            {removeBtn(() => round.remove(i))}
          </div>
        ))}
        {round.items.length > 0 && subtotal(round.items.reduce((s,d)=>s+calcRoundArea(d),0))}
        {addBtn('Add Round Duct', round.add)}
      </div>
    ),
    oval: (
      <div>
        {oval.items.length === 0 && emptyRows('No oval ducts added.')}
        {oval.items.map((d, i) => (
          <div key={i} style={rowStyle}>
            <NInput label="Major Axis (in)" value={d.majorAxis || ''} onChange={v => oval.update(i, { majorAxis: n(v) })} />
            <NInput label="Minor Axis (in)" value={d.minorAxis || ''} onChange={v => oval.update(i, { minorAxis: n(v) })} />
            <NInput label="Length (in)" value={d.length || ''} onChange={v => oval.update(i, { length: n(v) })} />
            {removeBtn(() => oval.remove(i))}
          </div>
        ))}
        {addBtn('Add Oval Duct', oval.add)}
      </div>
    ),
    transition: (
      <div>
        {trans.items.length === 0 && emptyRows('No transition ducts added.')}
        {trans.items.map((d, i) => (
          <div key={i} style={rowStyle}>
            <NInput label="Inlet W (in)" value={d.inletWidth || ''} onChange={v => trans.update(i, { inletWidth: n(v) })} />
            <NInput label="Inlet H (in)" value={d.inletHeight || ''} onChange={v => trans.update(i, { inletHeight: n(v) })} />
            <NInput label="Outlet W (in)" value={d.outletWidth || ''} onChange={v => trans.update(i, { outletWidth: n(v) })} />
            <NInput label="Outlet H (in)" value={d.outletHeight || ''} onChange={v => trans.update(i, { outletHeight: n(v) })} />
            <NInput label="Length (in)" value={d.length || ''} onChange={v => trans.update(i, { length: n(v) })} />
            {removeBtn(() => trans.remove(i))}
          </div>
        ))}
        {addBtn('Add Transition Duct', trans.add)}
      </div>
    ),
    'radial-elbow': (
      <div>
        {radial.items.length === 0 && emptyRows('No radial elbows added.')}
        {radial.items.map((e, i) => (
          <div key={i} style={rowStyle}>
            <NInput label="Inlet W (in)" value={e.inletWidth || ''} onChange={v => radial.update(i, { inletWidth: n(v) })} />
            <NInput label="Inlet H (in)" value={e.inletHeight || ''} onChange={v => radial.update(i, { inletHeight: n(v) })} />
            <NInput label="Inside R (in)" value={e.insideRadius || ''} onChange={v => radial.update(i, { insideRadius: n(v) })} />
            <NInput label="Outside R (in)" value={e.outsideRadius || ''} onChange={v => radial.update(i, { outsideRadius: n(v) })} />
            <NInput label="Angle (°)" value={e.elbowAngle || ''} onChange={v => radial.update(i, { elbowAngle: n(v) })} />
            <NInput label="Qty" value={e.quantity || ''} onChange={v => radial.update(i, { quantity: n(v) })} />
            {removeBtn(() => radial.remove(i))}
          </div>
        ))}
        {addBtn('Add Radial Elbow', radial.add)}
      </div>
    ),
    'rectangular-elbow': (
      <div>
        {rectElbow.items.length === 0 && emptyRows('No rectangular elbows added.')}
        {rectElbow.items.map((e, i) => (
          <div key={i} style={rowStyle}>
            <NInput label="Overall W (in)" value={e.overallWidth || ''} onChange={v => rectElbow.update(i, { overallWidth: n(v) })} />
            <NInput label="Overall L (in)" value={e.overallLength || ''} onChange={v => rectElbow.update(i, { overallLength: n(v) })} />
            <NInput label="Inlet W (in)" value={e.inletWidth || ''} onChange={v => rectElbow.update(i, { inletWidth: n(v) })} />
            <NInput label="Inlet H (in)" value={e.inletHeight || ''} onChange={v => rectElbow.update(i, { inletHeight: n(v) })} />
            <NInput label="Outlet W (in)" value={e.outletWidth || ''} onChange={v => rectElbow.update(i, { outletWidth: n(v) })} />
            <NInput label="Outlet H (in)" value={e.outletHeight || ''} onChange={v => rectElbow.update(i, { outletHeight: n(v) })} />
            <NInput label="Qty" value={e.quantity || ''} onChange={v => rectElbow.update(i, { quantity: n(v) })} />
            {removeBtn(() => rectElbow.remove(i))}
          </div>
        ))}
        {addBtn('Add Rectangular Elbow', rectElbow.add)}
      </div>
    ),
    'round-elbow': (
      <div>
        {roundElbow.items.length === 0 && emptyRows('No round elbows added.')}
        {roundElbow.items.map((e, i) => (
          <div key={i} style={rowStyle}>
            <NInput label="Diameter (in)" value={e.diameter || ''} onChange={v => roundElbow.update(i, { diameter: n(v) })} />
            <NInput label="Radius (in)" value={e.radius || ''} onChange={v => roundElbow.update(i, { radius: n(v) })} />
            <NInput label="Angle (°)" value={e.angle || ''} onChange={v => roundElbow.update(i, { angle: n(v) })} />
            <NInput label="Qty" value={e.quantity || ''} onChange={v => roundElbow.update(i, { quantity: n(v) })} />
            {removeBtn(() => roundElbow.remove(i))}
          </div>
        ))}
        {addBtn('Add Round Elbow', roundElbow.add)}
      </div>
    ),
  };

  return (
    <div>
      {/* Page head */}
      <Card style={{ marginBottom: 16 }} styles={{ body: { padding: '16px 24px' } }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <div>
            <Typography.Title level={4} style={{ margin: 0 }}>
              <ColumnWidthOutlined style={{ color: token.colorPrimary, marginRight: 8 }} />DALT
            </Typography.Title>
            <Typography.Text type="secondary">Duct air leakage test · SMACNA</Typography.Text>
          </div>
        </div>
      </Card>

      {error && <Alert type="error" showIcon message={error} closable onClose={() => setError('')} style={{ marginBottom: 16 }} />}

      <Card title={<span><SlidersOutlined /> Test Parameters</span>} style={{ marginBottom: 16 }}>
        <div style={paramGrid}>
          {paramField('Section Label',
            <Input value={label} onChange={e => setLabel(e.target.value)} placeholder="e.g. Supply Duct Zone 1" />,
            undefined, true)}
          {paramField('Test Pressure ("WC)',
            <Input type="number" status={fieldErrors.testPressure ? 'error' : undefined} value={testPressure}
              onChange={e => { setTestPressure(e.target.value); setFieldErrors(f => ({ ...f, testPressure: '' })); }} />,
            'testPressure')}
          {paramField('Leakage Class',
            <Input type="number" status={fieldErrors.leakageClass ? 'error' : undefined} value={leakageClass}
              onChange={e => { setLeakageClass(e.target.value); setFieldErrors(f => ({ ...f, leakageClass: '' })); }} />,
            'leakageClass')}
          {paramField('Pressure Drop ("WC)',
            <Input type="number" status={fieldErrors.pressureDrop ? 'error' : undefined} value={pressureDrop}
              onChange={e => { setPressureDrop(e.target.value); setFieldErrors(f => ({ ...f, pressureDrop: '' })); }} />,
            'pressureDrop')}
          {paramField('Orifice Size (in)', (
            <>
              <Select
                style={{ width: '100%' }} allowClear placeholder="Select…"
                value={orificeSize || undefined}
                options={[
                  ...Object.keys(CALIBRATION_CONSTANTS).map(k => ({ value: k, label: `${k}"` })),
                  { value: 'custom', label: 'Other (manual)' },
                ]}
                onChange={v => handleOrificeSizeChange(v ?? '')}
              />
              {orificeSize === 'custom' && (
                <Input style={{ marginTop: 6 }} value={customOrifice} onChange={e => setCustomOrifice(e.target.value)} placeholder='e.g. 0.75"' />
              )}
            </>
          ))}
          {paramField((
            <span>
              Calibration Constant
              {orificeSize && CALIBRATION_CONSTANTS[orificeSize] && (
                <Typography.Text type="secondary"> (auto: {CALIBRATION_CONSTANTS[orificeSize]})</Typography.Text>
              )}
            </span>
          ), (
            <Input type="number" status={fieldErrors.calibConst ? 'error' : undefined} value={calibConst}
              onChange={e => { setCalibConst(e.target.value); setFieldErrors(f => ({ ...f, calibConst: '' })); }}
              placeholder={orificeSize && CALIBRATION_CONSTANTS[orificeSize] ? String(CALIBRATION_CONSTANTS[orificeSize]) : 'Enter value'} />
          ), 'calibConst')}
          {paramField('Test Date', <Input type="date" value={testDate} onChange={e => setTestDate(e.target.value)} />)}
          {paramField('Witnessed By', <Input value={witnessedBy} onChange={e => setWitnessedBy(e.target.value)} />)}
        </div>

        <Alert
          type="info" showIcon style={{ marginTop: 14 }}
          message={<span><b>SMACNA quick reference:</b> Leakage Class (LC) 6 = max allowable for most duct systems · LC 3 = tighter seal · Test Pressure 1.0" WC for systems ≤2" WC static.</span>}
        />

        <div style={{ marginTop: 14 }}>
          <Button icon={<ToolOutlined />} onClick={() => setShowEquipment(s => !s)}>
            {showEquipment ? 'Hide' : 'Show'} Test Equipment Details
          </Button>
          {showEquipment && (
            <Card type="inner" title="Test Equipment" style={{ marginTop: 10 }}>
              <div style={paramGrid}>
                {paramField('Manufacturer', <Input value={eqMfr} onChange={e => setEqMfr(e.target.value)} placeholder="e.g. Oriflow" />)}
                {paramField('Model Number', <Input value={eqModel} onChange={e => setEqModel(e.target.value)} placeholder="e.g. Cobra" />)}
                {paramField('Serial Number', <Input value={eqSerial} onChange={e => setEqSerial(e.target.value)} />)}
                {paramField('Last Calibration', <Input type="date" value={eqLastCal} onChange={e => setEqLastCal(e.target.value)} />)}
                {paramField('Next Calibration', <Input type="date" value={eqNextCal} onChange={e => setEqNextCal(e.target.value)} />)}
                {paramField('Orifice Serial', <Input value={orificeSerial} onChange={e => setOrificeSerial(e.target.value)} />)}
              </div>
            </Card>
          )}
        </div>
      </Card>

      <Card
        title={<span><ApartmentOutlined /> Duct &amp; Elbow Sections</span>}
        extra={<Tag color="green">{totalDuctCount} section{totalDuctCount !== 1 ? 's' : ''}</Tag>}
        style={{ marginBottom: 16 }}
      >
        <Tabs
          activeKey={ductTab}
          onChange={k => setDuctTab(k as DuctTab)}
          items={DUCT_TABS.map(t => ({ key: t.id, label: t.label, children: tabContent[t.id] }))}
        />

        {totalDuctCount > 0 && (
          <div style={{ marginTop: 14, padding: '10px 14px', background: token.colorFillTertiary, borderRadius: 8, fontSize: 13 }}>
            <strong>Section summary:</strong>&nbsp;
            {rect.items.length > 0 && `Rect ${rect.items.reduce((s,d)=>s+calcRectArea(d),0).toFixed(1)} · `}
            {round.items.length > 0 && `Round ${round.items.reduce((s,d)=>s+calcRoundArea(d),0).toFixed(1)} · `}
            {oval.items.length > 0 && `Oval ${oval.items.reduce((s,d)=>s+calcOvalArea(d),0).toFixed(1)} · `}
            {trans.items.length > 0 && `Trans ${trans.items.reduce((s,d)=>s+calcTransArea(d),0).toFixed(1)} · `}
            {radial.items.length > 0 && `Radial ${radial.items.reduce((s,e)=>s+calcRadialArea(e),0).toFixed(1)} · `}
            {rectElbow.items.length > 0 && `Rect elbow ${rectElbow.items.reduce((s,e)=>s+calcRectElbowArea(e),0).toFixed(1)} · `}
            {roundElbow.items.length > 0 && `Round elbow ${roundElbow.items.reduce((s,e)=>s+calcRoundElbowArea(e),0).toFixed(1)} · `}
            <strong>Total: {ductTotal.toFixed(2)} ft²</strong>
          </div>
        )}

        {livePreview && (
          <>
            <Alert
              type={livePreview.passed ? 'success' : 'error'} showIcon
              style={{ marginTop: 16, marginBottom: 12 }}
              message={<b>{livePreview.passed ? 'PASS' : 'FAIL'} — {livePreview.percentOfAllowable.toFixed(1)}% of allowable</b>}
              description="Live preview · SMACNA leakage"
            />
            <Row gutter={16}>
              <Col flex="1 1 0">
                <Card size="small"><Statistic title="Surface area" value={(livePreview.surfaceArea/144).toFixed(1)} suffix="ft²" valueStyle={{ fontSize: 20 }} /></Card>
              </Col>
              <Col flex="1 1 0">
                <Card size="small"><Statistic title="Actual leakage" value={livePreview.actualLeakage.toFixed(1)} suffix="CFM" valueStyle={{ fontSize: 20 }} /></Card>
              </Col>
              <Col flex="1 1 0">
                <Card size="small"><Statistic title="Allowable" value={livePreview.allowableLeakage.toFixed(1)} suffix="CFM" valueStyle={{ fontSize: 20 }} /></Card>
              </Col>
              <Col flex="1 1 0">
                <Card size="small"><Statistic title="Test LC" value={livePreview.testLeakageClass.toFixed(2)} valueStyle={{ fontSize: 20 }} /></Card>
              </Col>
            </Row>
          </>
        )}

        <div style={{ marginTop: 16, maxWidth: 420 }}>
          <div style={{ fontSize: 13, marginBottom: 6 }}>Notes</div>
          <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional" />
        </div>

        <div style={{ marginTop: 14 }}>
          <Button
            type="primary"
            icon={saveSuccess ? <CheckCircleOutlined /> : <CalculatorOutlined />}
            loading={saving}
            disabled={saving || saveSuccess}
            onClick={handleSave}
          >
            {saveSuccess ? 'Saved!' : 'Calculate & Save'}
          </Button>
        </div>
      </Card>

      {/* Saved test results */}
      <div style={{ marginTop: 24 }}>
        <Typography.Title level={5}><HistoryOutlined /> Test Results</Typography.Title>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>
        ) : results.length === 0 ? (
          <Typography.Text type="secondary">No tests saved yet.</Typography.Text>
        ) : results.map(r => (
          <Card
            key={r.id}
            style={{ marginBottom: 16 }}
            title={(
              <span>
                {r.testResults?.passed
                  ? <CheckCircleOutlined style={{ color: token.colorSuccess, marginRight: 8 }} />
                  : <CloseCircleOutlined style={{ color: token.colorError, marginRight: 8 }} />}
                {r.sectionLabel || 'Untitled Section'}
              </span>
            )}
            extra={(
              <Space>
                <Tag color={r.testResults?.passed ? 'green' : 'red'} style={{ marginInlineEnd: 0 }}>
                  {r.testResults?.passed ? 'PASS' : 'FAIL'}
                </Tag>
                <Button size="small" icon={<PrinterOutlined />} onClick={() => handlePrint(r)} title="Print Report" aria-label="Print report" />
                <Popconfirm title="Delete this test result?" onConfirm={() => handleDelete(r.id!)} okText="Delete" okButtonProps={{ danger: true }}>
                  <Button size="small" danger icon={<DeleteOutlined />} title="Delete" aria-label="Delete result" />
                </Popconfirm>
              </Space>
            )}
          >
            {r.testResults && (
              <>
                <Row gutter={[16, 16]}>
                  <Col xs={12} md={4}><Statistic title="Surface area" value={(r.testResults.surfaceArea / 144).toFixed(1)} suffix="ft²" valueStyle={{ fontSize: 20 }} /></Col>
                  <Col xs={12} md={4}><Statistic title="Actual leakage" value={r.testResults.actualLeakage.toFixed(1)} suffix="CFM" valueStyle={{ fontSize: 20 }} /></Col>
                  <Col xs={12} md={4}><Statistic title="Allowable" value={r.testResults.allowableLeakage.toFixed(1)} suffix="CFM" valueStyle={{ fontSize: 20 }} /></Col>
                  <Col xs={12} md={4}><Statistic title="Of allowable" value={r.testResults.percentOfAllowable.toFixed(1)} suffix="%" valueStyle={{ fontSize: 20 }} /></Col>
                  <Col xs={12} md={4}><Statistic title="Leakage factor" value={r.testResults.leakageFactor.toFixed(3)} valueStyle={{ fontSize: 20 }} /></Col>
                  <Col xs={12} md={4}><Statistic title="Test LC" value={r.testResults.testLeakageClass.toFixed(2)} valueStyle={{ fontSize: 20 }} /></Col>
                </Row>
                <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 12 }}>
                  {[r.testParameters.equipmentManufacturer, r.testParameters.equipmentModel, r.testParameters.equipmentSerial].filter(Boolean).length > 0 && (
                    <span><ToolOutlined /> {[r.testParameters.equipmentManufacturer, r.testParameters.equipmentModel, r.testParameters.equipmentSerial].filter(Boolean).join(' · ')} · </span>
                  )}
                  {r.testParameters.testDate} · P={r.testParameters.testPressure}"WC · LC={r.testParameters.leakageClass}
                  {r.testParameters.witnessedBy && ` · Witnessed: ${r.testParameters.witnessedBy}`}
                  {r.testParameters.orificeSize && ` · Orifice ${r.testParameters.orificeSize}"`}
                </Typography.Text>
                {r.notes && (
                  <Typography.Text type="secondary" italic style={{ fontSize: 12, display: 'block', marginTop: 6 }}>{r.notes}</Typography.Text>
                )}
                <Button
                  size="small" type="text" style={{ marginTop: 12 }}
                  icon={expandedResults[r.id!] ? <UpOutlined /> : <DownOutlined />}
                  onClick={() => setExpandedResults(prev => ({ ...prev, [r.id!]: !prev[r.id!] }))}
                >
                  {expandedResults[r.id!] ? 'Hide' : 'View'} duct details
                </Button>
                {expandedResults[r.id!] && (
                  <Table<DetailRow>
                    size="small"
                    style={{ marginTop: 12 }}
                    pagination={false}
                    columns={detailColumns}
                    dataSource={detailRows(r)}
                  />
                )}
              </>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
};

export default DaltModule;
